import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { formatDistanceToNow } from "date-fns";
import { 
  Bell, 
  Info, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  X, 
  CheckCheck,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { InAppNotification } from "@shared/schema";

const typeIcons = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
};

const typeColors = {
  info: "text-blue-500",
  success: "text-green-500",
  warning: "text-yellow-500",
  error: "text-red-500",
};

interface NotificationBellProps {
  variant?: "icon" | "full";
  location?: "sidebar" | "mobile" | "collapsed";
}

export default function NotificationBell({ variant = "icon", location = "sidebar" }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const [limitDrafts, setLimitDrafts] = useState<Record<string, string>>({});
  const [, setLocation] = useLocation();

  const { data: notifications = [], isLoading } = useQuery<InAppNotification[]>({
    queryKey: ['/api/notifications/in-app'],
  });

  const { data: countData } = useQuery<{ count: number }>({
    queryKey: ['/api/notifications/in-app/count'],
  });

  const unreadCount = countData?.count ?? 0;

  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('POST', `/api/notifications/in-app/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/in-app'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/in-app/count'] });
      queryClient.invalidateQueries({ queryKey: ['/api/assistants'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await apiRequest('POST', '/api/notifications/in-app/read-all');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/in-app'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/in-app/count'] });
      queryClient.invalidateQueries({ queryKey: ['/api/assistants'] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('POST', `/api/notifications/in-app/${id}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/in-app'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/in-app/count'] });
    },
  });

  const approvalMutation = useMutation({
    mutationFn: async ({ approvalId, notificationId, dailyLimitCents }: { approvalId: string; notificationId: string; dailyLimitCents?: number }) => {
      await apiRequest("POST", `/api/spending/approvals/${approvalId}/approve`, dailyLimitCents === undefined
        ? {}
        : { dailyLimitCents });
      await apiRequest("POST", `/api/notifications/in-app/${notificationId}/dismiss`).catch(() => undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/in-app'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/in-app/count'] });
    },
  });

  const denyApprovalMutation = useMutation({
    mutationFn: async ({ approvalId, notificationId }: { approvalId: string; notificationId: string }) => {
      await apiRequest("POST", `/api/spending/approvals/${approvalId}/deny`);
      await apiRequest("POST", `/api/notifications/in-app/${notificationId}/dismiss`).catch(() => undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/in-app'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/in-app/count'] });
    },
  });

  const handleNotificationClick = (notification: InAppNotification) => {
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.id);
    }
    if (notification.actionUrl) {
      setOpen(false);
      setLocation(notification.actionUrl);
    }
  };

  const handleDismiss = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    dismissMutation.mutate(id);
  };

  const visibleNotifications = notifications.filter(n => !n.isDismissed).slice(0, 10);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid={`button-notification-bell-${location}`}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium"
              data-testid="badge-unread-count"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0" 
        align="end"
        data-testid="popover-notifications"
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold" data-testid="text-notifications-title">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2"
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isPending}
              data-testid="button-mark-all-read"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        <ScrollArea className="h-[300px]">
          {isLoading ? (
            <div className="p-4 text-center text-muted-foreground" data-testid="text-loading">
              Loading...
            </div>
          ) : visibleNotifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground" data-testid="text-no-notifications">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {visibleNotifications.map((notification) => {
                const TypeIcon = typeIcons[notification.type as keyof typeof typeIcons] || Info;
                const typeColor = typeColors[notification.type as keyof typeof typeColors] || typeColors.info;
                
                return (
                  <div
                    key={notification.id}
                    className={cn(
                      "p-3 hover:bg-muted/50 cursor-pointer transition-colors relative group",
                      !notification.isRead && "bg-primary/5"
                    )}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      {!notification.isRead && (
                        <div 
                          className="absolute left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary"
                          data-testid={`indicator-unread-${notification.id}`}
                        />
                      )}
                      <div className={cn("flex-shrink-0 mt-0.5", typeColor)}>
                        <TypeIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0 pr-6">
                        <p 
                          className="text-sm font-medium truncate"
                          data-testid={`text-notification-title-${notification.id}`}
                        >
                          {notification.title}
                        </p>
                        <p 
                          className="text-xs text-muted-foreground line-clamp-2"
                          data-testid={`text-notification-message-${notification.id}`}
                        >
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span 
                            className="text-xs text-muted-foreground"
                            data-testid={`text-notification-time-${notification.id}`}
                          >
                            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                          </span>
                          {notification.actionUrl && (
                            <ExternalLink className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                        {notification.entityType === "relationship_spending_approval" && notification.entityId && (
                          <div className="mt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => approvalMutation.mutate({ approvalId: notification.entityId!, notificationId: notification.id })}
                                disabled={approvalMutation.isPending || denyApprovalMutation.isPending}
                                data-testid={`button-approve-spending-${notification.entityId}`}
                              >
                                Approve today
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => denyApprovalMutation.mutate({ approvalId: notification.entityId!, notificationId: notification.id })}
                                disabled={approvalMutation.isPending || denyApprovalMutation.isPending}
                                data-testid={`button-deny-spending-${notification.entityId}`}
                              >
                                Deny
                              </Button>
                            </div>
                            <div className="flex gap-2">
                              <Input
                                className="h-7 text-xs"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="New daily limit ($)"
                                value={limitDrafts[notification.entityId] ?? ""}
                                onChange={(e) => setLimitDrafts((current) => ({ ...current, [notification.entityId!]: e.target.value }))}
                                data-testid={`input-adjust-spending-${notification.entityId}`}
                              />
                              <Button
                                size="sm"
                                variant="secondary"
                                className="h-7 whitespace-nowrap text-xs"
                                onClick={() => {
                                  const cents = Math.round(Number(limitDrafts[notification.entityId!] ?? "") * 100);
                                  if (Number.isFinite(cents) && cents >= 0) {
                                    approvalMutation.mutate({ approvalId: notification.entityId!, notificationId: notification.id, dailyLimitCents: cents });
                                  }
                                }}
                                disabled={approvalMutation.isPending}
                                data-testid={`button-adjust-spending-${notification.entityId}`}
                              >
                                Adjust limit
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => handleDismiss(e, notification.id)}
                        data-testid={`button-dismiss-${notification.id}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <Separator />
        <div className="p-2">
          <Button
            variant="ghost"
            className="w-full text-sm"
            onClick={() => {
              setOpen(false);
              setLocation('/notification-settings');
            }}
            data-testid="link-see-all-notifications"
          >
            See all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
